import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { Shield, Check, Zap, Building2, Rocket } from 'lucide-react';
import * as api from '../lib/api';

const PLAN_ICONS = { basico: Zap, profesional: Building2, empresarial: Rocket };
const PLAN_COLORS = { basico: '#3b82f6', profesional: '#6366f1', empresarial: '#8b5cf6' };

export default function PlanesPage() {
  const { user, logout } = useAuth();
  const { dispatch } = useAppState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [currentSub, setCurrentSub] = useState(null);
  const [resuming, setResuming] = useState(false);
  const [changingTo, setChangingTo] = useState(null);
  const [buyingExtra, setBuyingExtra] = useState(false);

  // Override global overflow:hidden
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    const root = document.getElementById('root');
    if (root) root.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      if (root) root.style.overflow = '';
    };
  }, []);

  // Read pending onboarding to recommend the right plan
  const pendingRaw = localStorage.getItem('nom035_pending_onboarding');
  const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
  const numTrab = pending?.centro?.numTrabajadores || 0;
  const numCentros = pending?.empresa?.totalCentros || 0;
  const empresaNombre = pending?.empresa?.razonSocial || '';

  const PRECIO_CENTRO_EXTRA = { basico: 1490, profesional: 1290, empresarial: 990 };

  // Find the cheapest plan considering extras
  const getRecommended = () => {
    if ((!numTrab && !numCentros) || plans.length === 0) return 'profesional';
    const sorted = [...plans].sort((a, b) => a.price_mxn - b.price_mxn);
    // Only consider plans that fit the worker count (can't add-on workers)
    const viable = sorted.filter(p => p.max_trabajadores >= numTrab || numTrab === 0);
    if (viable.length === 0) return sorted[sorted.length - 1].key;
    // Pick cheapest total (plan + extras)
    let best = null, bestTotal = Infinity;
    viable.forEach(p => {
      const extras = Math.max(0, numCentros - p.max_centros);
      const total = p.price_mxn + extras * (PRECIO_CENTRO_EXTRA[p.key] || 990);
      if (total < bestTotal) { bestTotal = total; best = p.key; }
    });
    return best || 'profesional';
  };
  const recommended = getRecommended();

  // After payment: check for pending onboarding data, create empresa + centro.
  // createEmpresa es idempotente (el backend reusa la empresa existente del
  // usuario si ya hay una), y createCentro reusa el mismo id generado en el
  // wizard — así que llamar esto más de una vez para el mismo pending es seguro.
  const completeOnboarding = async () => {
    const raw = localStorage.getItem('nom035_pending_onboarding');
    if (!raw) { window.location.href = '/'; return; }
    try {
      const pending = JSON.parse(raw);
      // Create empresa
      const result = await api.createEmpresa(pending.empresa);
      const empresaId = result.id;
      api.setStoredEmpresaId(empresaId);
      dispatch({ type: ACTION_TYPES.SET_EMPRESA_ID, payload: empresaId });
      dispatch({ type: ACTION_TYPES.SET_EMPRESA, payload: pending.empresa });
      // Create centro
      await api.createCentro(empresaId, pending.centro);
      dispatch({ type: ACTION_TYPES.ADD_CENTRO, payload: pending.centro });
      dispatch({ type: ACTION_TYPES.SET_CENTRO_ACTUAL, payload: 0 });
      // Cleanup
      localStorage.removeItem('nom035_pending_onboarding');
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Onboarding completion error:', err);
      // No se borra nom035_pending_onboarding ni se redirige a ciegas: si el
      // pago ya se cobró, borrar esto dejaría al usuario sin forma de
      // terminar su alta sin volver a pagar. Se reintenta solo al recargar
      // (ver el useEffect de abajo) o el usuario puede reintentar manualmente.
      setResuming(false);
      alert('No se pudo terminar de configurar tu cuenta (' + (err.message || 'error de red') + '). Tu pago ya se registró — recarga esta página para reintentar; si el problema persiste, contáctanos.');
    }
  };

  useEffect(() => {
    api.getPlans().then(setPlans).catch(console.error);
    if (user) {
      api.getSubscription().then(sub => {
        if (sub && sub.active) {
          setCurrentSub(sub);
          // Ya tiene plan activo pero llegó aquí con datos de onboarding
          // pendientes (ej. el navegador se cerró justo después de pagar,
          // antes de crear la empresa/centro) — reanudar automáticamente en
          // vez de dejarlo atorado sin poder volver a "comprar" el mismo plan.
          if (pending) { setResuming(true); completeOnboarding(); }
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle return from Stripe checkout — wait for auth to be ready
  useEffect(() => {
    if (searchParams.get('payment') === 'success' && user) {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) return;
      setVerifying(true);
      const verify = async () => {
        try {
          // Small delay to ensure token is fresh
          await new Promise(r => setTimeout(r, 1000));
          const result = await api.verifyPayment(sessionId);
          if (result.activated) {
            await completeOnboarding();
            return;
          } else {
            setTimeout(async () => {
              const retry = await api.verifyPayment(sessionId);
              if (retry.activated) {
                await completeOnboarding();
                return;
              } else {
                setVerifying(false);
                alert('No se pudo verificar el pago. Intenta recargar la página.');
              }
            }, 3000);
          }
        } catch (err) {
          console.error('Verify error:', err);
          setVerifying(false);
        }
      };
      verify();
    } else if (searchParams.get('payment') === 'success' && !user) {
      setVerifying(true); // Show loading while auth restores
    }
  }, [searchParams, navigate, user]);

  const handleSelect = async (planKey, extraCentros = 0) => {
    setLoading(planKey);
    try {
      const baseUrl = window.location.origin;
      const { url } = await api.createCheckout(planKey, `${baseUrl}/planes?payment=success&session_id={CHECKOUT_SESSION_ID}`, `${baseUrl}/planes?payment=canceled`, extraCentros);
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error al procesar: ' + err.message);
      setLoading(null);
    }
  };

  // Para quien ya tiene suscripción activa: modifica la suscripción existente
  // en vez de pasar por Checkout (que crearía una segunda suscripción y lo
  // dejaría pagando ambas a la vez).
  const handleChangePlan = async (planKey) => {
    setChangingTo(planKey);
    try {
      const result = await api.changePlan(planKey);
      if (result.changed) {
        const sub = await api.getSubscription();
        if (sub && sub.active) setCurrentSub(sub);
        alert('Tu plan se actualizó correctamente.');
      }
    } catch (err) {
      console.error('Change plan error:', err);
      alert('No se pudo cambiar de plan: ' + (err.message || 'error desconocido'));
    }
    setChangingTo(null);
  };

  const handleBuyExtraCentro = async () => {
    setBuyingExtra(true);
    try {
      const result = await api.comprarCentroAdicional(1);
      const sub = await api.getSubscription();
      if (sub && sub.active) setCurrentSub(sub);
      alert(`Listo — ahora tienes ${result.extra_centros} centro(s) adicional(es) en tu plan.`);
    } catch (err) {
      console.error('Buy extra centro error:', err);
      alert('No se pudo agregar el centro adicional: ' + (err.message || 'error desconocido'));
    }
    setBuyingExtra(false);
  };

  if (verifying || resuming) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #ede9fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>✓</div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Configurando tu cuenta...</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Verificando pago y preparando tu plataforma. No cierres esta ventana.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #ede9fe 100%)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Shield size={24} style={{ color: '#6366f1' }} />
            <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b' }}>NOM-035</span>
          </div>
          <h1 style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1e293b', margin: '0 0 8px', lineHeight: 1.2 }}>Elige tu plan</h1>
          <p style={{ color: '#64748b', fontSize: '1rem', margin: '0 0 4px', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
            Cumple con la NOM-035-STPS-2018 de forma simple y profesional. Pago anual + IVA.
          </p>
          {pending && (numTrab > 0 || numCentros > 0) && (
            <div style={{ display: 'inline-block', marginTop: 12, padding: '8px 18px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <span style={{ fontSize: '0.85rem', color: '#15803d', fontWeight: 500 }}>
                {empresaNombre} — {numTrab} trabajadores · {numCentros} centro{numCentros > 1 ? 's' : ''} — necesitas {numTrab <= 15 ? 'Guia I' : numTrab <= 50 ? 'Guia I + II' : 'Guia I + III'}
              </span>
            </div>
          )}
          {currentSub && currentSub.active && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginTop: 12, padding: '10px 18px', background: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
              <span style={{ fontSize: '0.85rem', color: '#4338ca', fontWeight: 500 }}>
                {'¿Necesitas un centro más en tu plan actual? +$'}{(PRECIO_CENTRO_EXTRA[currentSub.plan_key] || 990).toLocaleString('es-MX')}{'/año'}
              </span>
              <button onClick={handleBuyExtraCentro} disabled={buyingExtra} style={{
                padding: '6px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6,
                fontSize: '0.78rem', fontWeight: 600, cursor: buyingExtra ? 'wait' : 'pointer', whiteSpace: 'nowrap',
              }}>
                {buyingExtra ? 'Procesando...' : 'Agregar centro adicional'}
              </button>
            </div>
          )}
          {user && (
            <div style={{ marginTop: 12, fontSize: '0.82rem', color: '#94a3b8' }}>
              {user.email} · <button onClick={logout} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }}>Cerrar sesión</button>
              {currentSub && (
                <span> · <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500 }}>Ir al panel →</button></span>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'start' }}>
          {plans.map(plan => {
            const Icon = PLAN_ICONS[plan.key] || Zap;
            const color = PLAN_COLORS[plan.key] || '#6366f1';
            const isRec = plan.key === recommended;
            const workerInsufficient = numTrab > 0 && plan.max_trabajadores < numTrab;
            const extraCentros = numCentros > 0 ? Math.max(0, numCentros - plan.max_centros) : 0;
            const extraCost = extraCentros * (PRECIO_CENTRO_EXTRA[plan.key] || 990);
            const totalPrice = plan.price_mxn + extraCost;
            const isLoading = loading === plan.key;
            const isCurrent = currentSub && currentSub.active && currentSub.plan_key === plan.key;
            const isChangingThis = changingTo === plan.key;
            const anyBusy = loading !== null || changingTo !== null;
            const buttonLabel = isCurrent ? '✓ Plan actual'
              : workerInsufficient ? `Insuficiente para ${numTrab} trabajadores`
              : currentSub && currentSub.active ? (isChangingThis ? 'Cambiando...' : 'Cambiar a este plan')
              : (isLoading ? 'Redirigiendo...' : 'Contratar plan');
            const handleClick = () => {
              if (isCurrent || workerInsufficient || anyBusy) return;
              if (currentSub && currentSub.active) handleChangePlan(plan.key);
              else handleSelect(plan.key, extraCentros);
            };

            return (
              <div key={plan.key} style={{
                background: '#fff', borderRadius: 16, padding: 32,
                border: isRec ? `2px solid ${color}` : '1px solid #e5e7eb',
                boxShadow: isRec ? `0 8px 32px ${color}22` : '0 2px 12px rgba(0,0,0,0.04)',
                position: 'relative', transform: isRec ? 'scale(1.03)' : 'none',
              }}>
                {isRec && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', padding: '4px 16px', background: color, color: '#fff', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {numTrab > 0 ? 'IDEAL PARA TU EMPRESA' : 'RECOMENDADO'}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={20} style={{ color }} /></div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{plan.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{plan.max_trabajadores > 9000 ? 'Trabajadores ilimitados' : `Hasta ${plan.max_trabajadores} trabajadores`} · {plan.max_centros} centro{plan.max_centros > 1 ? 's' : ''}</div>
                  </div>
                </div>
                <div style={{ marginBottom: extraCentros > 0 ? 8 : 20 }}>
                  <span style={{ fontSize: '2.4rem', fontWeight: 800, color: '#1e293b' }}>${plan.price_mxn.toLocaleString('es-MX')}</span>
                  <span style={{ fontSize: '0.88rem', color: '#94a3b8' }}>{' /a\u00f1o + IVA'}</span>
                </div>
                {extraCentros > 0 && !workerInsufficient && (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, marginBottom: 16, fontSize: '0.78rem', lineHeight: 1.5 }}>
                    <div style={{ color: '#92400e', fontWeight: 600 }}>{'+ '}{extraCentros}{' centro'}{extraCentros > 1 ? 's' : ''}{' extra'}{extraCentros > 1 ? 's' : ''}{' \u00d7 $'}{(PRECIO_CENTRO_EXTRA[plan.key] || 990).toLocaleString('es-MX')}{' = $'}{extraCost.toLocaleString('es-MX')}{'/a\u00f1o'}</div>
                    <div style={{ color: '#b45309', fontWeight: 700, marginTop: 4 }}>{'Total: $'}{totalPrice.toLocaleString('es-MX')}{'/a\u00f1o + IVA'}</div>
                  </div>
                )}
                <button onClick={handleClick} disabled={anyBusy || isCurrent || workerInsufficient} style={{
                  width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none',
                  background: workerInsufficient ? '#f1f5f9' : (isCurrent ? '#10b981' : (isRec ? color : '#f1f5f9')),
                  color: workerInsufficient ? '#94a3b8' : (isCurrent || isRec ? '#fff' : '#374151'),
                  fontSize: '0.92rem', fontWeight: 600,
                  cursor: workerInsufficient || isCurrent ? 'default' : (anyBusy ? 'wait' : 'pointer'),
                  marginBottom: 20, opacity: (anyBusy && !isLoading && !isChangingThis) || workerInsufficient ? 0.5 : 1,
                }}>
                  {buttonLabel}
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Check size={16} style={{ color: '#10b981', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.4 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0', fontSize: '0.75rem', color: '#94a3b8' }}>
                  {'Centro adicional: $'}{(PRECIO_CENTRO_EXTRA[plan.key] || 990).toLocaleString('es-MX')}{'/a\u00f1o'}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 40, fontSize: '0.78rem', lineHeight: 1.8 }}>
          <div style={{ color: '#64748b', fontWeight: 500 }}>Todos los precios son anuales + IVA</div>
          <div style={{ color: '#94a3b8' }}>Solicita tu factura por <a href="https://wa.me/5215519357446?text=Hola%2C%20quiero%20solicitar%20mi%20factura%20NOM-035" target="_blank" rel="noopener" style={{ color: '#25d366', fontWeight: 600, textDecoration: 'none' }}>WhatsApp</a></div>
          <div style={{ color: '#cbd5e1', marginTop: 4 }}>Pago seguro con Stripe · Cancela cuando quieras · Perspicaz Labs</div>
        </div>
      </div>
    </div>
  );
}
